import {useQuery} from "@tanstack/react-query";
import {getMonthlyUsageSummary} from "../../api/api";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import {toast} from "react-toastify";
import {useContext, useEffect, useMemo, useState} from "react";
import AuthContext from "../../context/AuthContext";
import Loader from "../../components/Loader";
import RecordsNotFound from "../../components/RecordsNotFound";
import {Button} from "reactstrap";

const monthFormatter = new Intl.DateTimeFormat("en-US", {month: "long"});

function formatCurrency(amount) {
    return amount === null || amount === undefined ? "-" : `Rs ${amount}`;
}

function formatMonthYear(month, year) {
    const date = new Date(year, month - 1, 1);
    return monthFormatter.format(date) + " " + year;
}

function SummaryCard({title, value, subtitle}) {
    return (
        <div className="col-md-6 col-xl-3 mb-3">
            <div className="card border h-100 mb-0">
                <div className="card-body">
                    <p className="text-muted mb-2">{title}</p>
                    <h3 className="mb-1">{value ?? 0}</h3>
                    <p className="mb-0 text-muted">{subtitle}</p>
                </div>
            </div>
        </div>
    );
}

function MonthlyUsageSummaryTab() {
    const {refreshToken} = useContext(AuthContext);
    const [page, setPage] = useState(1);
    const [selectedSummaryId, setSelectedSummaryId] = useState("");

    const {status, data, error, isFetching, refetch} = useQuery({
        queryKey: ['monthly-usage-summary', page],
        queryFn: () => getMonthlyUsageSummary(page),
        keepPreviousData: true,
        retry: false,
        onError: (err => {
            handleUnauthorized401QueryError(err, refreshToken, refetch);
            if (err.response.status !== 401) {
                toast.error(`An error occurred! ${err?.response?.data?.detail}`);
            }
        }),
    });

    useEffect(() => {
        if (!data?.results?.length) {
            setSelectedSummaryId("");
            return;
        }

        const selectedExists = data.results.some(summary => summary.id === selectedSummaryId);
        if (!selectedSummaryId || !selectedExists) {
            setSelectedSummaryId(data.results[0].id);
        }
    }, [data, selectedSummaryId]);

    const selectedSummary = useMemo(() => {
        if (!data?.results?.length) {
            return null;
        }
        return data.results.find(summary => summary.id === selectedSummaryId) ?? data.results[0];
    }, [data, selectedSummaryId]);

    const rows = selectedSummary ? [
        {
            category: "Products",
            count: selectedSummary.active_products ?? 0,
            unitPrice: selectedSummary.active_product_unit_price,
            beforeTax: selectedSummary.active_product_total_amount_before_tax,
            tax: selectedSummary.active_product_tax,
            afterTax: selectedSummary.active_product_total_amount_after_tax,
        },
        {
            category: "Add-On Products",
            count: selectedSummary.active_add_on_products ?? 0,
            unitPrice: selectedSummary.active_add_on_product_unit_price,
            beforeTax: selectedSummary.active_add_on_total_amount_before_tax,
            tax: selectedSummary.active_add_on_product_tax,
            afterTax: selectedSummary.active_add_on_total_amount_after_tax,
        },
        {
            category: "Product Groups",
            count: selectedSummary.active_product_groups ?? 0,
            unitPrice: selectedSummary.active_product_group_unit_price,
            beforeTax: selectedSummary.active_product_group_total_amount_before_tax,
            tax: selectedSummary.active_product_group_tax,
            afterTax: selectedSummary.active_product_group_total_amount_after_tax,
        },
        {
            category: "Customers",
            count: selectedSummary.active_customers ?? 0,
            unitPrice: selectedSummary.active_customer_unit_price,
            beforeTax: selectedSummary.active_customer_total_amount_before_tax,
            tax: selectedSummary.active_customer_tax,
            afterTax: selectedSummary.active_customer_total_amount_after_tax,
        },
    ] : [];

    const totalTax = rows.reduce((sum, row) => sum + Number(row.tax ?? 0), 0).toFixed(2);
    const totalBeforeTax = rows.reduce((sum, row) => sum + Number(row.beforeTax ?? 0), 0).toFixed(2);
    const totalAfterTax = rows.reduce((sum, row) => sum + Number(row.afterTax ?? 0), 0).toFixed(2);

    return (
        <>
            {status === 'loading' && <Loader size={'default'}/>}

            {status === 'success' || error?.response?.status === 401 ? (
                selectedSummary ? (
                    <div className="mt-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                            <div>
                                <h4 className="mb-1">Monthly Usage Overview</h4>
                                <p className="text-muted mb-0">See what contributes to your bill for a billing cycle.</p>
                            </div>
                            <div className="d-flex align-items-center">
                                <label className="mb-0 mr-2">Billing Cycle</label>
                                <select
                                    className="form-control"
                                    value={selectedSummary.id}
                                    onChange={(e) => setSelectedSummaryId(e.target.value)}
                                >
                                    {data?.results?.map(summary => (
                                        <option key={summary.id} value={summary.id}>
                                            {formatMonthYear(summary.month, summary.year)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="row">
                            <SummaryCard title="Active Products" value={selectedSummary.active_products ?? 0}
                                         subtitle={`Rate: ${formatCurrency(selectedSummary.active_product_unit_price)} / item`}/>
                            <SummaryCard title="Active Add-On Products" value={selectedSummary.active_add_on_products ?? 0}
                                         subtitle={`Rate: ${formatCurrency(selectedSummary.active_add_on_product_unit_price)} / item`}/>
                            <SummaryCard title="Active Product Groups" value={selectedSummary.active_product_groups ?? 0}
                                         subtitle={`Rate: ${formatCurrency(selectedSummary.active_product_group_unit_price)} / item`}/>
                            <SummaryCard title="Active Customers" value={selectedSummary.active_customers ?? 0}
                                         subtitle={`Rate: ${formatCurrency(selectedSummary.active_customer_unit_price)} / item`}/>
                        </div>

                        <div className="row">
                            <div className="col-lg-8 mb-3">
                                <div className="card border h-100 mb-0">
                                    <div className="card-body">
                                        <h5 className="mb-3">Usage Breakdown</h5>
                                        <div className="table-responsive">
                                            <table className="table table-centered mb-0">
                                                <thead className="font-13 bg-light text-muted">
                                                <tr>
                                                    <th>Category</th>
                                                    <th>Count</th>
                                                    <th>Unit Price</th>
                                                    <th>Before Tax</th>
                                                    <th>Tax</th>
                                                    <th>After Tax</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {rows.map(row => (
                                                    <tr key={row.category}>
                                                        <td>{row.category}</td>
                                                        <td>{row.count}</td>
                                                        <td>{formatCurrency(row.unitPrice)}</td>
                                                        <td>{formatCurrency(row.beforeTax)}</td>
                                                        <td>{formatCurrency(row.tax)}</td>
                                                        <td>{formatCurrency(row.afterTax)}</td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4 mb-3">
                                <div className="card border h-100 mb-0">
                                    <div className="card-body">
                                        <h5 className="mb-3">Billing Snapshot</h5>
                                        <p className="text-muted mb-2">Period</p>
                                        <h4 className="mb-3">{formatMonthYear(selectedSummary.month, selectedSummary.year)}</h4>

                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">Subtotal Before Tax</span>
                                            <span>{formatCurrency(totalBeforeTax)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">Total GST</span>
                                            <span>{formatCurrency(totalTax)}</span>
                                        </div>
                                        <hr/>
                                        <div className="d-flex justify-content-between">
                                            <strong>Estimated Total</strong>
                                            <strong>{formatCurrency(totalAfterTax)}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end align-items-center">
                            <Button
                                type="button"
                                className="btn bx-btn-primary waves-effect waves-light mr-2"
                                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                disabled={page === 1 || isFetching}
                            >
                                Previous
                            </Button>
                            <Button
                                type="button"
                                className="btn bx-btn-primary waves-effect waves-light"
                                onClick={() => setPage(prev => prev + 1)}
                                disabled={!data?.next || isFetching}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                ) : <RecordsNotFound/>
            ) : null}
        </>
    );
}

export default MonthlyUsageSummaryTab;
